import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon, AlertCircleIcon } from 'lucide-react';
export function Pricing() {
  const [expandedAddOn, setExpandedAddOn] = useState<string | null>(null);
  const toggleAddOn = (section: string) => {
    setExpandedAddOn(expandedAddOn === section ? null : section);
  };
  const scrollToConsultation = () => {
    window.location.href = 'mailto:info@bright-pixxel.com?subject=New%20Enquiry';
  };
  const webDesignFeatures = ['Up to 5 pages', 'Responsive mobile design', 'Enquiry form', 'Basic SEO setup', 'Blog setup option', '1 business email address', 'Fast loading speeds'];
  const ecommerceFeatures = ['Up to 20 products included', 'PayPal/Stripe integration', 'Basic product search', 'Responsive shop layout', 'Blog (optional)', 'Basic SEO', 'Priority email support'];
  const maintenanceFeatures = ['Hosting & domain management', 'SSL renewal', 'Security monitoring', 'Monthly backups', 'Plugin/theme updates', 'Basic email support'];
  const consultationPoints = ['Review your website goals', 'Check if you need booking systems, payment links, or forms.', 'Discuss whether you need content writing', 'Review design inspiration websites', 'Identify SEO needs', 'Help you choose colours and branding', 'Confirm your pages and features', 'Provide a tailored quote based on your exact requirements'];
  const contentWritingAddOns = ['Per page (up to 400 words)', 'Product descriptions', 'About/Story writing', 'SEO keyword research'];
  const brandingAddOns = ['Logo creation', 'Brand colour palette + typography selection', 'Favicon design'];
  const pagesAddOns = ['Extra web pages (beyond the included amount)'];
  const featuresAddOns = ['Additional forms', 'Payment links/buttons', 'Booking system integration', 'Social media integration', 'Photo editing'];
  const faqs = [{
    question: 'How does the free consultation work?',
    answer: "Book a 30-minute call where we discuss your needs, goals, and vision. We'll provide a personalised quote based on your specific requirements."
  }, {
    question: 'Are there any hidden fees?',
    answer: "No. All costs are transparent and agreed upon during your consultation. You'll receive a detailed quote before any work begins."
  }, {
    question: 'Can I upgrade my package later?',
    answer: "Yes, you can add features or pages at any time. We'll provide pricing for any additions you need."
  }, {
    question: 'Do you offer payment plans?',
    answer: 'We require 50% payment upfront before starting your project, and the remaining 50% upon completion. This ensures commitment from both sides and allows us to deliver your website without delays.'
  }, {
    question: 'What if I need something not listed?',
    answer: "We create custom solutions for unique requirements. Discuss your needs during the free consultation and we'll provide a tailored quote."
  }];
  return <>
      <Helmet data-id="element-1743">
        <title data-id="element-1744">Web Design Pricing | Bright Pixxel Studio</title>
        <meta name="description" content="Clear, honest web design and e-commerce pricing from £495. No hidden fees — book a free consultation for a package tailored to your business." data-id="element-1745" />
        <link rel="canonical" href="https://bright-pixxel.com/pricing" data-id="element-1746" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-1747" />
        <meta property="og:type" content="website" data-id="element-1748" />
        <meta property="og:title" content="Web Design Pricing | Bright Pixxel Studio" data-id="element-1749" />
        <meta property="og:description" content="Clear, honest web design and e-commerce pricing from £495. No hidden fees — book a free consultation for a package tailored to your business." data-id="element-1750" />
        <meta property="og:url" content="https://bright-pixxel.com/pricing" data-id="element-1751" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-1752" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1753" />
        <meta property="og:image:width" content="1200" data-id="element-1754" />
        <meta property="og:image:height" content="630" data-id="element-1755" />
        <meta property="og:image:type" content="image/png" data-id="element-1756" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - Transparent Web Design Pricing" data-id="element-1757" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-1758" />
        <meta name="twitter:title" content="Web Design Pricing | Bright Pixxel Studio" data-id="element-1759" />
        <meta name="twitter:description" content="Clear, honest web design and e-commerce pricing from £495. No hidden fees — book a free consultation for a package tailored to your business." data-id="element-1760" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1761" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - Transparent Web Design Pricing" data-id="element-1762" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-white" data-id="element-1763">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1764">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1765">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-bold mb-6 text-[#0C1B26]" data-id="element-1766">
              Simple, Transparent Pricing
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1767">
              Every website is built specifically around your business needs. A
              tailored package is created after your free consultation.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1768">
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-[#0C1B26] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" data-id="element-1769">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-gray-50" data-id="element-1770">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1771">
          <div className="max-w-4xl mx-auto" data-id="element-1772">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-1773">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="flex flex-col items-center" data-id="element-1774">
                <CheckIcon size={32} className="text-[#0DC3DC] mb-2" data-id="element-1775" />
                <p className="font-bold text-gray-900" data-id="element-1776">Transparent pricing</p>
                <p className="text-sm text-gray-600" data-id="element-1777">No hidden fees</p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="flex flex-col items-center" data-id="element-1778">
                <CheckIcon size={32} className="text-[#0DC3DC] mb-2" data-id="element-1779" />
                <p className="font-bold text-gray-900" data-id="element-1780">Fast turnaround</p>
                <p className="text-sm text-gray-600" data-id="element-1781">7–14 days</p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col items-center" data-id="element-1782">
                <CheckIcon size={32} className="text-[#0DC3DC] mb-2" data-id="element-1783" />
                <p className="font-bold text-gray-900" data-id="element-1784">Flexible packages</p>
                <p className="text-sm text-gray-600" data-id="element-1785">Built for your needs</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards - Side by Side */}
      <section className="py-16 bg-white" data-id="element-1786">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1787">
          <div className="max-w-6xl mx-auto" data-id="element-1788">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-12" data-id="element-1789">
              <div className="inline-flex items-center gap-2 bg-[#0DC3DC]/10 px-4 py-2 rounded-full mb-4" data-id="element-1790">
                <AlertCircleIcon size={20} className="text-[#0DC3DC]" data-id="element-1791" />
                <span className="text-sm font-medium text-[#0DC3DC]" data-id="element-1792">
                  Starting prices shown below
                </span>
              </div>
              <h2 className="text-3xl font-bold text-[#0C1B26] mb-3" data-id="element-1793">
                Choose Your Starting Package
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1794">
                These are minimum starting prices. Your final quote will be
                tailored to your exact requirements during the free
                consultation.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1795">
              {/* Launch */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-1796">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1797">Launch</h3>
                <div className="mb-4" data-id="element-1798">
                  <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-1799">£495</p>
                </div>
                <ul className="space-y-2 mb-6 flex-grow" data-id="element-1800">
                  {['Up to 4 pages', 'Brand-aligned design', 'Contact form', 'Mobile-first build', 'Basic SEO setup', 'Domain connection'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-1801">
                      <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1802" />
                      <span data-id="element-1803">{feature}</span>
                    </li>)}
                </ul>
                <a href="/contact" className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-1804">
                  Get your free homepage design
                </a>
              </motion.div>

              {/* Standard */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-[#212C34] rounded-lg p-6 border-2 border-[#0DC3DC] shadow-xl flex flex-col relative" data-id="element-1805">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2" data-id="element-1806">
                  <span className="bg-[#0DC3DC] text-white px-4 py-1 rounded-full text-xs font-bold" data-id="element-1807">
                    MOST POPULAR
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1808">Standard</h3>
                <div className="mb-4" data-id="element-1809">
                  <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-1810">£895</p>
                </div>
                <ul className="space-y-2 mb-6 flex-grow" data-id="element-1811">
                  {['Up to 8 pages', 'Custom design and build', 'Content integration', 'SEO-ready structure', 'Meta titles and descriptions', 'Favicon & social links', 'Full launch setup'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-1812">
                      <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1813" />
                      <span data-id="element-1814">{feature}</span>
                    </li>)}
                </ul>
                <a href="/contact" className="w-full bg-[#0DC3DC] hover:bg-[#26D0E8] text-white py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-1815">
                  Get your free homepage design
                </a>
              </motion.div>

              {/* Growth */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-[#212C34] rounded-lg p-6 border border-[#D4AF37] shadow-lg flex flex-col" data-id="element-1816">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1817">Growth</h3>
                <div className="mb-4" data-id="element-1818">
                  <p className="text-4xl font-bold text-[#0DC3DC]" data-id="element-1819">£1,395</p>
                </div>
                <ul className="space-y-2 mb-6 flex-grow" data-id="element-1820">
                  {['Up to 10 pages', 'Everything in Standard', 'Keyword-mapped page structure', 'Google Business Profile setup'].map((feature, index) => <li key={index} className="flex items-start gap-2 text-white text-sm" data-id="element-1821">
                      <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1822" />
                      <span data-id="element-1823">{feature}</span>
                    </li>)}
                </ul>
                <a href="/contact" className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3 rounded-lg font-bold transition-all duration-300 text-center block" data-id="element-1824">
                  Get your free homepage design
                </a>
              </motion.div>
            </div>

            {/* Highlight Box */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-[#0DC3DC]/10 border-l-4 border-[#0DC3DC] p-6 rounded-lg mb-8" data-id="element-1825">
              <p className="text-gray-900 font-medium text-center" data-id="element-1826">
                E-commerce and bespoke builds quoted individually — from £990.
                Additional pages £75 each.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Consultation Section */}
      <section id="consultation-section" className="py-16 bg-[#212C34]" data-id="element-1827">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1828">
          <div className="max-w-3xl mx-auto" data-id="element-1829">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1830">
              <h2 className="text-3xl font-bold mb-3 text-white text-center" data-id="element-1831">
                What We'll Cover in Your Free 30-Minute Consultation
              </h2>
              <p className="text-white/80 text-center mb-8" data-id="element-1832">
                This consultation increases trust and ensures you get exactly
                what you need.
              </p>

              <div className="bg-[#212C34] rounded-lg p-8 border border-[#D4AF37] mb-8" data-id="element-1833">
                <p className="text-white font-medium mb-4" data-id="element-1834">
                  During your consultation we will:
                </p>
                <ul className="space-y-3" data-id="element-1835">
                  {consultationPoints.map((point, index) => <li key={index} className="flex items-start gap-3 text-white" data-id="element-1836">
                      <CheckIcon size={20} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1837" />
                      <span data-id="element-1838">{point}</span>
                    </li>)}
                </ul>
              </div>

              <div className="text-center" data-id="element-1839">
                <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-[#0C1B26] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" data-id="element-1840">
                  Get your free homepage design
                </a>
                <p className="text-white/70 text-sm mt-4" data-id="element-1841">
                  This shows professionalism and reduces pricing arguments
                  later.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Optional Add-Ons */}
      <section className="py-16 bg-white" data-id="element-1842">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1843">
          <div className="max-w-3xl mx-auto" data-id="element-1844">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-center mb-8" data-id="element-1845">
              <h2 className="text-3xl font-bold mb-3 text-[#0C1B26]" data-id="element-1846">
                Optional Add-Ons
              </h2>
              <p className="text-gray-600" data-id="element-1847">
                Shown for transparency, not complexity. Add what you need.
              </p>
            </motion.div>

            <div className="space-y-4" data-id="element-1848">
              {/* Content Writing */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1849">
                <button onClick={() => toggleAddOn('content')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1850">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1851">
                    Content Writing
                  </span>
                  {expandedAddOn === 'content' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1852" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1853" />}
                </button>
                <AnimatePresence data-id="element-1854">
                  {expandedAddOn === 'content' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1855">
                      <div className="p-6 bg-white" data-id="element-1856">
                        <ul className="space-y-2" data-id="element-1857">
                          {contentWritingAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1858">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1859" />
                              <span data-id="element-1860">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Branding */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1861">
                <button onClick={() => toggleAddOn('branding')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1862">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1863">
                    Branding
                  </span>
                  {expandedAddOn === 'branding' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1864" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1865" />}
                </button>
                <AnimatePresence data-id="element-1866">
                  {expandedAddOn === 'branding' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1867">
                      <div className="p-6 bg-white" data-id="element-1868">
                        <ul className="space-y-2" data-id="element-1869">
                          {brandingAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1870">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1871" />
                              <span data-id="element-1872">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Pages */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1873">
                <button onClick={() => toggleAddOn('pages')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1874">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1875">Pages</span>
                  {expandedAddOn === 'pages' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1876" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1877" />}
                </button>
                <AnimatePresence data-id="element-1878">
                  {expandedAddOn === 'pages' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1879">
                      <div className="p-6 bg-white" data-id="element-1880">
                        <ul className="space-y-2" data-id="element-1881">
                          {pagesAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1882">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1883" />
                              <span data-id="element-1884">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>

              {/* Features */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1885">
                <button onClick={() => toggleAddOn('features')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1886">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-1887">
                    Features
                  </span>
                  {expandedAddOn === 'features' ? <ChevronUpIcon size={24} className="text-[#0DC3DC]" data-id="element-1888" /> : <ChevronDownIcon size={24} className="text-[#0DC3DC]" data-id="element-1889" />}
                </button>
                <AnimatePresence data-id="element-1890">
                  {expandedAddOn === 'features' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1891">
                      <div className="p-6 bg-white" data-id="element-1892">
                        <ul className="space-y-2" data-id="element-1893">
                          {featuresAddOns.map((addon, index) => <li key={index} className="flex items-start gap-2 text-gray-700" data-id="element-1894">
                              <CheckIcon size={16} className="text-[#0DC3DC] mt-0.5 flex-shrink-0" data-id="element-1895" />
                              <span data-id="element-1896">{addon}</span>
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mt-8 text-center" data-id="element-1897">
              <p className="text-gray-600 italic mb-6" data-id="element-1898">
                These feel small, predictable, and fair.
              </p>
              <a href="/contact" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-[#0C1B26] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105" data-id="element-1899">
                Get your free homepage design
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50" data-id="element-1900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1901">
          <div className="max-w-3xl mx-auto" data-id="element-1902">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl font-bold mb-8 text-[#0C1B26] text-center" data-id="element-1903">
              Frequently Asked Questions
            </motion.h2>

            <div className="space-y-4" data-id="element-1904">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white rounded-lg p-6 shadow-sm" data-id="element-1905">
                  <h3 className="text-lg font-bold mb-3 text-gray-900" data-id="element-1906">
                    {faq.question}
                  </h3>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1907">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#212C34]" data-id="element-1908">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1909">
          <div className="max-w-2xl mx-auto text-center" data-id="element-1910">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1911">
              <h2 className="text-3xl font-bold mb-4 text-white" data-id="element-1912">
                Ready to Start Your Project?
              </h2>
              <p className="text-white/80 mb-8 text-lg" data-id="element-1913">
                Book your free consultation today and get a personalised quote
                tailored to your exact needs.
              </p>
              <a href="mailto:info@bright-pixxel.com?subject=Free%20Consultation%20Request" className="inline-block bg-[#0DC3DC] hover:bg-[#26D0E8] text-white px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1914">
                Start Your Project Today
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}